import React, { FC, memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectBusinessDate } from '@hkm/components/Menu/PropertySelector/domain/selectors';
import MaintenanceStateBadge from '@hkm/components/shared/MaintenanceStateBadge/MaintenanceStateBadge';
import MaintenanceStatusBadge from '@hkm/components/shared/MaintenanceStatusBadge/MaintenanceStatusBadge';
import MaintenanceDetailsFormBody from '@hkm/components/shared/Templates/Maintenance/MaintenanceDetailsTile/MaintenanceDetailsTileForm';
import { MaintenanceUpdateFormData } from '@hkm/components/shared/Templates/Maintenance/MaintenanceDetailsTile/models/maintenanceFormData';
import { MaintenanceUpdateFieldDisability } from '@hkm/components/shared/Templates/Maintenance/MaintenanceDetailsTile/models/MaintenanceUpdateFieldDisability';
import { mapToMaintenanceFormData } from '@hkm/components/shared/Templates/Maintenance/MaintenanceDetailsTile/models/mappers/mapToMaintenanceFormData';
import { mapToUpdateMaintenance } from '@hkm/components/shared/Templates/Maintenance/MaintenanceDetailsTile/models/mappers/mapToUpdateMaintenance';
import { ValidationStatuses } from '@hkm/shared/interfaces/validationStatuses';
import { Validator } from '@hkm/shared/validation/validator';
import {
  isDateNotEmpty,
  isEndDateBeforeStartDate,
  isNonEmptyString,
  isStartDateAfterEndDate,
  isStartDateBeforeBusinessDate
} from '@hkm/shared/validation/validators';
import { UnifiedMaintenanceDetails } from '@hkm/types/maintenance/models/UnifiedMaintenanceDetails';
import { UnifiedRoomDetails } from '@hkm/types/room/model/UnifiedRoomDetails';
import { clone, isEqual } from 'lodash';

import {
  RoomMaintenanceState,
  RoomMaintenanceStatus,
  UpdateMaintenances
} from '@ac/library-api';
import {
  AcFormElement,
  AcFormGroup,
  AcFormRow
} from '@ac/mobile-components/dist/components/form-element';
import {
  AcTile,
  AcTileVariant
} from '@ac/mobile-components/dist/components/tile';
import { Icon } from '@ac/mobile-components/dist/enums';
import { Styleable } from '@ac/mobile-components/dist/interfaces/componentProps';
import { formatTestSelector } from '@ac/mobile-components/dist/utils';
import {
  Form,
  ValidationResult,
  ValidationSchema
} from '@ac/react-infrastructure';

export interface MaintenanceDetailsTileProps extends Styleable {
  readonly?: boolean;
  variant?: AcTileVariant;
  hideStateBadge?: boolean;
  unifiedRoomDetails: UnifiedRoomDetails;
  maintenanceDetails: UnifiedMaintenanceDetails;
  disableFields?: MaintenanceUpdateFieldDisability;
  onSubmit?(formData: UpdateMaintenances): void;
}

interface Statuses extends ValidationStatuses {
  fromTime: ValidationResult[];
  toTime: ValidationResult[];
  statusCode: ValidationResult[];
  reasonCode: ValidationResult[];
  comment: ValidationResult[];
  returnStatusCode: ValidationResult[];
}

const Schema: ValidationSchema<Statuses> = {
  fromTime: [
    isDateNotEmpty,
    isStartDateAfterEndDate,
    isStartDateBeforeBusinessDate
  ],
  toTime: [isDateNotEmpty, isEndDateBeforeStartDate],
  comment: [isNonEmptyString]
};

const MaintenanceDetailsTile: FC<MaintenanceDetailsTileProps> = (
  props: MaintenanceDetailsTileProps
) => {
  const { t } = useTranslation();
  const businessDate = useSelector(selectBusinessDate);
  const initialData: MaintenanceUpdateFormData = mapToMaintenanceFormData(
    props.maintenanceDetails,
    props.unifiedRoomDetails,
    businessDate
  );
  const previousFormState: MaintenanceUpdateFormData = clone(initialData);
  const isMaintenanceActive =
    props.maintenanceDetails.state?.code === RoomMaintenanceState.Active;
  const ignoreFields = isMaintenanceActive ? ['fromTime'] : [];
  const testSelectorPrefix = 'maintenance-details';

  const [validator] = useState<Validator<MaintenanceUpdateFormData, Statuses>>(
    new Validator(Schema, ignoreFields)
  );
  const validate = useCallback(
    (values: MaintenanceUpdateFormData) => validator.validate(values),
    []
  );

  const onSubmit = (formData: MaintenanceUpdateFormData) => {
    if (props.onSubmit && !isEqual(previousFormState, formData)) {
      props.onSubmit(mapToUpdateMaintenance(formData));
    }
  };

  return (
    <AcTile
      title={t('ROOM_DETAILS.MAINTENANCE')}
      icon={Icon.Eod}
      className={props.className}
      style={props.style}
      variant={props.variant}
    >
      <AcFormGroup>
        <AcFormRow>
          {props.maintenanceDetails.status && (
            <AcFormElement
              label={t('ROOM_DETAILS.MAINTENANCE_STATUS')}
              testSelector={formatTestSelector(
                testSelectorPrefix,
                'maintenanceStatus'
              )}
            >
              <MaintenanceStatusBadge
                status={
                  props.maintenanceDetails.status.code as RoomMaintenanceStatus
                }
                fullTextInTile={true}
              />
            </AcFormElement>
          )}

          {props.maintenanceDetails.state && !props.hideStateBadge && (
            <AcFormElement
              label={t('ROOM_DETAILS.MAINTENANCE_STATE')}
              testSelector={formatTestSelector(
                testSelectorPrefix,
                'maintenanceState'
              )}
            >
              <MaintenanceStateBadge
                status={
                  props.maintenanceDetails.state.code as RoomMaintenanceState
                }
              />
            </AcFormElement>
          )}
        </AcFormRow>

        {props.maintenanceDetails.reason && (
          <AcFormElement
            label={t('ROOM_DETAILS.REASON')}
            testSelector={formatTestSelector(testSelectorPrefix, 'reason')}
          >
            {t('ROOM_DETAILS.REASON_TYPE_FORMAT', {
              code: props.maintenanceDetails.reason.code,
              description: props.maintenanceDetails.reason.description
            })}
          </AcFormElement>
        )}

        <Form
          initialValues={initialData}
          validate={validate}
          onSubmit={onSubmit}
        >
          {formRenderProps => (
            <MaintenanceDetailsFormBody
              formProps={formRenderProps}
              readonly={props.readonly}
              disableFields={props.disableFields}
            />
          )}
        </Form>
      </AcFormGroup>
    </AcTile>
  );
};

export default memo(MaintenanceDetailsTile);



// WEBPACK FOOTER //
// ./src/components/shared/Templates/Maintenance/MaintenanceDetailsTile/MaintenanceDetailsTile.tsx